import { useEffect, useRef, useState } from 'react';
import StyledButton from './StyledButton';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/i18n';

interface StyledExpandableTextProps {
  text: string;
  maxHeight: number;
  shouldDisplayAllText?: boolean;
  className?: string;
}

const StyledExpandableText = ({
  text,
  maxHeight,
  shouldDisplayAllText = true,
  className,
}: StyledExpandableTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const toggleReadMoreBtn = () => {
      if (shouldDisplayAllText) {
        setShowButton(false);
      } else if (textRef.current) {
        const currentHeight = textRef.current.scrollHeight;
        setShowButton(currentHeight > maxHeight - 10);
      }
    };
    toggleReadMoreBtn();
    window.addEventListener('resize', toggleReadMoreBtn);
    return () => window.removeEventListener('resize', toggleReadMoreBtn);
  }, [maxHeight, shouldDisplayAllText, textRef]);

  return (
    <div className="w-full">
      <span
        ref={textRef}
        className={cn(
          'text-sm 2xl:text-base font-normal text-gray-800 whitespace-pre-wrap',
          {
            [`line-clamp-1`]: !shouldDisplayAllText && !isExpanded,
          },
          className,
        )}
        style={{
          WebkitLineClamp: shouldDisplayAllText
            ? 'unset'
            : isExpanded || !showButton
              ? 'unset'
              : Math.floor(maxHeight / 24) - 1,
        }}
      >
        {text}
      </span>
      <div>
        {showButton && (
          <StyledButton
            variant="link"
            title={
              isExpanded
                ? t('ui.expandableText.showLess')
                : t('ui.expandableText.showMore')
            }
            className="p-0 text-brand-600 mt-2"
            onClick={() => setIsExpanded((prev) => !prev)}
            icon={
              isExpanded ? (
                <ChevronUp className="size-5 stroke-brand-600" />
              ) : (
                <ChevronDown className="size-5 stroke-brand-600" />
              )
            }
            iconPosition="right"
          />
        )}
      </div>
    </div>
  );
};

export default StyledExpandableText;
